import type { ContractRole, SingleView } from '@eformulieren/model'
import type { ReactNode } from 'react'
import { dossierView, readableBlockName } from './dossierValue.js'
import type { VerwijzingProps } from './kinds.js'

/** Eén vraag met de rijen die erbij horen, in de orde waarin de vraag voor het eerst voorkwam. */
export interface Group {
  readonly titel: string
  readonly rijen: readonly Record<string, unknown>[]
}

/**
 * `subgrid`: een alleen-lezen tabelweergave uit het dossier (R-08, R-62, R-137, D-73).
 *
 * Invullen loopt via de herhaalgroep (ontwerp 6.2); dit type toont wat er elders al staat. De
 * bron is een pad in een dossierdocument, en die documenten geeft de gastheer mee bij het
 * uitzetten (R-54, R-56). Dat mag het document van een eerder bouwblok zijn, maar juist ook dat
 * van het eigen blok: de reeks eerdere instanties van R-62 is één pad naar een lijst, en dit type
 * zet die reeks onder elkaar.
 *
 * **De groepering per vraag, en waarom ze niet altijd aanslaat.** R-62 vraagt "per vraag onder
 * elkaar, met de periode erbij", en welke eigenschap de vraag, de periode en het antwoord is
 * volgt uit de rol in het contract (D-73). Dat is de `rollen`-prop, en de aanroeper haalt hem uit
 * de bron van het veld: de publicatiepoort bevriest de rollen van elke reeks uit de registry in de
 * definitie (`freezeSeriesRoles` in de engine), ook van een extern of ingericht bouwblok, dus de pod
 * heeft ze zonder ooit een contract op te vragen. Een publicatie van daarvoor draagt ze niet en
 * groepeert alleen de door dit product gegenereerde `criteria`-eigenschap
 * (`seriesRolesFor` in `shell/seriesRoles.ts`). Zonder `rollen`, of zonder een eigenschap met rol
 * `vraag` erin, blijft het de platte tabel: dat is D-73's eigen uitzondering en geen gemakslezing,
 * "niet elke reeks is een vraag met antwoorden".
 *
 * **De kolommen komen uit de weergave van het bronveld als die er is, en anders uit de
 * gegevens.** Een tabel uit een herhaalgroep in een eerder blok krijgt de weergave van die
 * herhaalgroep mee, bevroren uit de publicatie waaronder de aanvrager invulde (R-55, R-100): dan
 * staan de kolommen in de orde van de definitie, met hun label in de taal van de lezer (R-143), en
 * krijgt elke cel de opmaak van haar rijveld. Zonder weergave valt er niets in te stellen, en is
 * het de vereniging van de sleutels van álle rijen, in de orde waarin ze voorkomen,
 * met `periode` vooraan omdat de eis hem apart noemt; zie `columnsOf` voor waarom de eerste rij
 * alleen niet genoeg is. Een rij die een kolom mist laat die cel leeg, en dat is eerlijker dan de
 * rij weglaten: de reeks moet compleet zijn. Bij groepering geldt hetzelfde per groep, en een
 * eigenschap zonder rol doet niet mee als kolom: ze staat in het document maar is geen antwoord
 * op een vraag (D-73, `revisieId` is zo'n eigenschap).
 *
 * **En niets verdwijnt stil.** Elementen die geen rij zijn worden geteld en gemeld, ook bij
 * groepering; de rijen die wél rijen zijn staan er gewoon. Tot M6.7 liet één zo'n element de hele
 * reeks verdwijnen met de melding dat er niets in het dossier stond, en dat was onwaar.
 */
export function Subgrid({
  taal: language,
  koppeling: link,
  uitkomst: outcome,
  bron: source,
  bronNaam: sourceName,
  roles,
  vraagLabels: questionLabels,
  elders: placedElsewhere,
}: VerwijzingProps): ReactNode {
  const read = rowsOf(outcome)
  const sourceNote =
    source === undefined ? null : (
      <span className="ef-subgrid__bron">
        {' '}
        ({language === 'nl' ? 'uit' : 'from'} {sourceName ?? readableBlockName(source)})
      </span>
    )

  if (read.rijen.length === 0) {
    // Geen reeks: geen bron, niets op het dossier, of een vorm die geen lijst objecten is. Geen
    // lege tabel met kolomkoppen die niemand heeft gedefinieerd; dat leest als een storing.
    return (
      <p className="ef-subgrid" id={link.id} aria-describedby={link['aria-describedby']}>
        {language === 'nl'
          ? 'Er staat hierover nog niets in het dossier.'
          : 'There is nothing about this in the case file yet.'}
        {sourceNote}
      </p>
    )
  }

  // De kolommen van de herhaalgroep zoals de aanvrager ze zag, als de weergave er is (R-55,
  // R-100): in de orde van de definitie, met hun label in de taal van de lezer (R-143) en per
  // kolom de opmaak van het rijveld. Zie `columnsOf` voor wat er zonder weergave gebeurt.
  const table = outcome.stand === 'waarde' && outcome.weergave?.soort === 'tabel' ? outcome.weergave : undefined
  const columns = columnsOf(read.rijen, table?.kolommen.map((column) => column.id))
  const columnView = new Map(table?.kolommen.map((column) => [column.id, column.weergave]))
  const headers = Object.fromEntries([...columnView].map(([id, view]) => [id, view.label[language]]))
  const questionColumn = roles === undefined ? undefined : columns.find((column) => roles[column] === 'vraag')
  const missed =
    read.overgeslagen === 0 ? null : (
      <span className="ef-subgrid__gemist">
        {' '}
        {language === 'nl'
          ? `(${read.overgeslagen} van de ${read.rijen.length + read.overgeslagen} regels zijn niet te tonen)`
          : `(${read.overgeslagen} of ${read.rijen.length + read.overgeslagen} entries cannot be shown)`}
      </span>
    )

  if (questionColumn === undefined) {
    return (
      <TableShell
        titel={<>{language === 'nl' ? 'Uit het dossier' : 'From the case file'}{sourceNote}{missed}</>}
        ariaLabel={ariaLabelFor(language, source)}
        kolommen={columns}
        headers={headers}
        columnView={columnView}
        rijen={read.rijen}
        koppeling={link}
        taal={language}
      />
    )
  }

  // Vanaf hier is er een vraag-eigenschap: groeperen in plaats van plat, met de periode en het
  // antwoord als kolom en de vraag zelf als titel per groep (D-73).
  const { kolommen: groupColumns, groepen: allGroups } = groupBy(read.rijen, columns, questionColumn, roles)
  // In de modus met historie staat een groep die al bij haar vraag staat hier niet nog eens (R-137).
  const groups = placedElsewhere === undefined ? allGroups : allGroups.filter((group) => !placedElsewhere.has(group.titel))
  const titleOf = (group: Group): string => questionLabels?.get(group.titel) ?? group.titel
  return (
    // `role="group"` en niet een kale `div`: zonder rol krijgt `aria-describedby` hier geen
    // vaste plek in de toegankelijkheidsboom om aan te hangen, en de hulptekst van het veld zou
    // dan alleen bij de platte weergave nog aankomen.
    <div id={link.id} role="group" aria-describedby={link['aria-describedby']}>
      {source === undefined && missed === null ? null : (
        <p className="ef-subgrid__intro">
          {language === 'nl' ? 'Uit het dossier' : 'From the case file'}
          {sourceNote}
          {missed}
        </p>
      )}
      {groups.length < allGroups.length ? (
        <p className="ef-subgrid__elders">
          {groups.length === 0
            ? language === 'nl'
              ? 'De eerdere antwoorden staan bij elk criterium.'
              : 'The earlier answers are shown with each criterion.'
            : language === 'nl'
              ? 'De eerdere antwoorden op de criteria van dit formulier staan bij elk criterium; hieronder de rest.'
              : 'The earlier answers to the criteria on this form are shown with each criterion; the rest is below.'}
        </p>
      ) : null}
      {groups.map((group) => (
        <TableShell
          key={group.titel}
          titel={titleOf(group)}
          // De bron blijft in het label, ook per groep: anders hoort wie via het landmark-menu
          // rechtstreeks in een groep landt (en de intro-alinea erboven overslaat) nooit waar de
          // tabel vandaan komt.
          ariaLabel={`${ariaLabelFor(language, source)}: ${titleOf(group)}`}
          kolommen={groupColumns}
          rijen={group.rijen}
          taal={language}
        />
      ))}
    </div>
  )
}

/** De reeks per vraag: de kolommen per groep en de groepen zelf (D-73). */
export interface GroupedSeries {
  readonly kolommen: readonly string[]
  readonly groepen: readonly Group[]
}

function groupBy(
  rows: readonly Record<string, unknown>[],
  columns: readonly string[],
  questionColumn: string,
  roles: Readonly<Record<string, ContractRole>> | undefined,
): GroupedSeries {
  return {
    kolommen: columns.filter(
      (column) => column !== questionColumn && (roles?.[column] === 'periode' || roles?.[column] === 'antwoord'),
    ),
    groepen: groupsOf(rows, questionColumn),
  }
}

/**
 * Dezelfde groepering als `Subgrid` tekent, als gegevens, voor wie de groepen elders neerzet: de
 * modus met historie zet elke groep bij haar vraag (R-137, `shell/history.ts`).
 *
 * Eén functie voor beide, zodat de reeks bij de vraag nooit andere kolommen of rijen heeft dan de
 * reeks in de bronsectie. Niets als de uitkomst geen reeks is of er geen eigenschap met rol `vraag`
 * is: dan is er geen vraag om iets bij te zetten (D-73).
 */
export function groupedSeries(
  outcome: VerwijzingProps['uitkomst'],
  roles: Readonly<Record<string, ContractRole>> | undefined,
): GroupedSeries | undefined {
  if (roles === undefined) return undefined
  const read = rowsOf(outcome)
  if (read.rijen.length === 0) return undefined
  const table = outcome.stand === 'waarde' && outcome.weergave?.soort === 'tabel' ? outcome.weergave : undefined
  const columns = columnsOf(read.rijen, table?.kolommen.map((column) => column.id))
  const questionColumn = columns.find((column) => roles[column] === 'vraag')
  return questionColumn === undefined ? undefined : groupBy(read.rijen, columns, questionColumn, roles)
}

/**
 * De eerdere antwoorden op één vraag, onder die vraag in de modus met historie (R-137, R-62).
 *
 * Dezelfde schil als een groep in `Subgrid`, met een naam die de vraag noemt en de bron: wie via
 * het landmark-menu hier landt, hoort waarover het gaat en waar het vandaan komt (R-58). De vraag
 * zelf staat er niet als kolom in, want het veld erboven is die vraag.
 */
export function QuestionHistory({
  taal: language,
  vraag: question,
  bron: source,
  kolommen: columns,
  rijen: rows,
}: {
  readonly taal: VerwijzingProps['taal']
  /** Het label van de vraag in de taal van de lezer, voor de naam van de regio. */
  readonly vraag: string
  readonly bron: string
  readonly kolommen: readonly string[]
  readonly rijen: readonly Record<string, unknown>[]
}): ReactNode {
  return (
    <div className="ef-historie">
      <TableShell
        titel={language === 'nl' ? 'Eerdere antwoorden' : 'Earlier answers'}
        ariaLabel={
          language === 'nl'
            ? `Eerdere antwoorden op ${question} (uit ${readableBlockName(source)})`
            : `Earlier answers to ${question} (from ${readableBlockName(source)})`
        }
        kolommen={columns}
        rijen={rows}
        taal={language}
      />
    </div>
  )
}

function ariaLabelFor(language: VerwijzingProps['taal'], source: string | undefined): string {
  if (source === undefined) return language === 'nl' ? 'Tabel uit het dossier' : 'Table from the case file'
  return language === 'nl' ? `Tabel uit het dossier (uit ${source})` : `Table from the case file (from ${source})`
}

/**
 * De scrollende schil om één tabel, gedeeld door de platte weergave en elke groep.
 *
 * **De schil schuift, dus hij is met het toetsenbord te bereiken.** Een gebied dat kan schuiven
 * zonder focus is voor wie geen muis gebruikt niet te lezen (WCAG 2.1.1, axe
 * scrollable-region-focusable). axe meldt dat pas als de tabel echt breder is dan zijn omhulsel,
 * en in deze suite is hij dat niet; in het beheerscherm van M6.12 viel het om precies dezelfde
 * constructie meteen op, en dat is de melding die M6.16 hier opschreef. Een naam erbij, want
 * focus zonder naam laat de schermlezer zeggen dat je ergens bent zonder te zeggen waar.
 *
 * **`display: block` op de `table` zelf hoort hier nooit te staan.** Dan is het element geen
 * tabelbox meer en verdwijnt de tabelrol in WebKit uit de toegankelijkheidsboom; dan doen de
 * rij- en kolomverbanden en het `scope` van de koppen niets meer. axe ziet dat niet, dus dit
 * stond na M6.4 mis met een groene test erbij. Een leesronde vond het.
 *
 * `koppeling` staat alleen op de platte weergave: één tabel is dan de hele control en draagt
 * daarom haar id. Bij groepering draagt de omringende `div` die id, want geen van de tabellen is
 * de control alleen.
 */
function TableShell({
  titel: title,
  ariaLabel,
  kolommen: columns,
  headers,
  columnView,
  rijen: rows,
  koppeling: link,
  taal: language,
}: {
  readonly titel: ReactNode
  readonly ariaLabel: string
  readonly kolommen: readonly string[]
  /** Het label per kolom, uit de weergave van het bronveld; zonder label is de kop de sleutel. */
  readonly headers?: Readonly<Record<string, string>>
  /** De weergave per kolom, voor de opmaak van de cellen. */
  readonly columnView?: ReadonlyMap<string, SingleView>
  readonly rijen: readonly Record<string, unknown>[]
  readonly koppeling?: VerwijzingProps['koppeling']
  readonly taal: VerwijzingProps['taal']
}): ReactNode {
  return (
    <div className="ef-subgrid-schil" tabIndex={0} role="region" aria-label={ariaLabel}>
      <table
        className="ef-subgrid-tabel"
        {...(link === undefined
          ? {}
          : { id: link.id, 'aria-describedby': link['aria-describedby'] })}
      >
        <caption className="ef-subgrid__caption">{title}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {headers?.[column] ?? column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // De index als sleutel, en dat is hier juist: dit is een bevroren reeks uit het
            // dossier die niet wordt gesorteerd of gefilterd, dus de plek is de identiteit.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index}>
              {columns.map((column) => (
                <td key={column}>
                  <Cell waarde={row[column]} taal={language} weergave={columnView?.get(column)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Groepeert de rijen op de waarde van de vraag-eigenschap, in de orde waarin een vraag voor het
 * eerst voorkwam (D-73). Niet alfabetisch: de reeks belooft oudste stand eerst, en een groep die
 * pas in een latere stand voor het eerst opduikt hoort dus na de groepen die al eerder bestonden.
 */
function groupsOf(rows: readonly Record<string, unknown>[], questionColumn: string): Group[] {
  const groups: Group[] = []
  const perTitle = new Map<string, Record<string, unknown>[]>()
  for (const row of rows) {
    const title = cellText(row[questionColumn])
    let list = perTitle.get(title)
    if (list === undefined) {
      list = []
      perTitle.set(title, list)
      groups.push({ titel: title, rijen: list })
    }
    list.push(row)
  }
  return groups
}

/**
 * De rijen uit de uitkomst, plus hoeveel elementen er geen rij waren.
 *
 * **Niet alles-of-niets, en dat is bij M6.7 rechtgezet.** Tot dan liet één element dat geen plat
 * object was de hele reeks verdwijnen, met de melding dat er niets in het dossier staat; dat is
 * onwaar en het maakt eerdere instanties onzichtbaar, precies wat R-62 verbiedt. Nu komen de
 * rijen die rijen zijn er wél in, en staat het aantal dat niet te tonen was in de caption. Een
 * regel stil weglaten en een regel met een melding weglaten zijn twee heel verschillende dingen.
 */
function rowsOf(outcome: VerwijzingProps['uitkomst']): {
  readonly rijen: Record<string, unknown>[]
  readonly overgeslagen: number
} {
  if (outcome.stand !== 'waarde') return { rijen: [], overgeslagen: 0 }
  const value = outcome.waarde
  if (!Array.isArray(value)) return { rijen: [], overgeslagen: 0 }
  const rows = value.filter(
    (row): row is Record<string, unknown> => typeof row === 'object' && row !== null && !Array.isArray(row),
  )
  return { rijen: rows, overgeslagen: value.length - rows.length }
}

/**
 * Welke kolommen er zijn, in welke orde.
 *
 * **De vereniging van alle rijen en niet alleen de eerste**, ook bij M6.7 rechtgezet. Kwam een
 * sleutel alleen in een latere rij voor, dan verdween die waarde zonder melding, en stond
 * `periode` niet in de eerste rij dan bestond die kolom voor de hele tabel niet, terwijl R-62
 * hem apart noemt. De orde is die van eerst gezien, met `periode` vooraan.
 */
function columnsOf(rows: readonly Record<string, unknown>[], fromDefinitie?: readonly string[]): string[] {
  const seen: string[] = []
  for (const row of rows) {
    for (const name of Object.keys(row)) if (!seen.includes(name)) seen.push(name)
  }
  if (fromDefinitie !== undefined) {
    // Met de weergave van de herhaalgroep: haar kolommen in de orde van de definitie, ook een
    // kolom die in geen enkele rij gevuld is, want die vraag stond er voor de aanvrager wel. Een
    // sleutel die de weergave niet kent komt erachter, met haar naam als kop: niets verdwijnt stil.
    return [...fromDefinitie, ...seen.filter((name) => !fromDefinitie.includes(name))]
  }
  return [...seen.filter((name) => name === 'periode'), ...seen.filter((name) => name !== 'periode')]
}

/**
 * De inhoud van één cel, in dezelfde vormen als een gewone verwijzing (`dossierwaarde.ts`).
 *
 * Een rij van een herhaalgroep uit een eerder blok draagt per rijveld zijn kale waarde: een lijst
 * keuzes van een multiselect, een lijst bijlagen, een ja of nee, of rich text met een regeleinde
 * tussen de blokken. Met de weergave van de kolom wordt een keuze haar label en een bedrag, getal
 * of datum opgemaakt zoals bij een gewone verwijzing. Tot het beoordelingsformulier meer velden
 * uit de aanvraag ging tonen bleef zo'n cel leeg terwijl de waarde er was. Een lijst staat hier
 * als opsomming met komma's en niet als eigen lijst, want een cel is geen plek voor een tweede
 * structuur; meer alinea's blijven alinea's. Een lege cel als er niets is, en nooit `[object
 * Object]`.
 */
function Cell({
  waarde: value,
  taal: language,
  weergave: sourceField,
}: {
  readonly waarde: unknown
  readonly taal: VerwijzingProps['taal']
  readonly weergave?: SingleView | undefined
}): ReactNode {
  const view = dossierView(value, language, sourceField)
  if (view === undefined) return null
  if (view.soort !== 'tekst') return view.items.join(', ')
  if (view.alineas.length === 1) return view.alineas[0]
  return view.alineas.map((paragraph, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <p key={index} className="ef-subgrid__alinea">
      {paragraph}
    </p>
  ))
}

/** De titel van een groep: de waarde van de vraag-eigenschap als tekst, en nooit `[object Object]`. */
function cellText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : ''
  if (typeof value === 'boolean') return String(value)
  return ''
}
